import { api, apiCall, apiPrefix, createQuery } from "@services/API/Requests";
import {
  MapAdminUser,
  MapBroadcastGroup,
  MapRoleData,
  MapUser,
  MapUserTenant,
} from "@services/API/MapData";
import { BlankTenantDepartmentData } from "@templates/BlankTenantDepartmentData";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { handleResponse } from "@services/API/StatusCodes";
import { USER_TYPE } from "@constants/Enums";
import { store } from "@services/StateManagement/Store";
import { USER_TYPE_STRING } from "@constants/Enums";

export const getUserData = async (
  setUser: (data: any) => void,
  setUserTenant: (data: any) => void,
  setTenantDepartment: (data: any) => void,
  setUserRole: (data: any) => void,
  setUserApps: (data: any) => void,
  setKeycloakGroups: (data: any) => void,
  setBroadcastGroup: (data: any) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  userId: string,
  useToken: boolean = false,
  loadKeycloakGroups: boolean = false,
) => {
  const uri: string = loadKeycloakGroups
    ? `${apiPrefix}/users/${userId}?load_keycloak_groups=false`
    : `${apiPrefix}/users/${userId}`;

  return apiCall({
    method: "get",
    url: uri,
    useToken,
    setLoading,
    onSuccess: (response) => {
      const userData = MapUser(response.data);

      setUser(userData);

      setBroadcastGroup(
        MapBroadcastGroup(response?.data?.broadcast_group || {}),
      );
      setKeycloakGroups(userData.user_keycloak_groups || []);
      if (
        response?.data?.internal_departments &&
        response?.data.internal_departments[0]
      ) {
        setTenantDepartment(
          MapUserTenant(response.data?.internal_departments[0]),
        );
      } else {
        setTenantDepartment(BlankTenantDepartmentData);
      }

      setUserRole(response.data ? MapRoleData(response.data) : []);

      if (
        response?.data?.user_type === USER_TYPE_STRING.KEYCLOAK_USER ||
        response?.data?.user_type === USER_TYPE.KEYCLOAK_USER
      ) {
        const userAppData = response?.data?.available_apps.map((app) => ({
          app_id: app.app_id,
        }));
        setUserApps(userAppData);
      }
    },
    onError: (errorResponse) => {
      setError(errorResponse);
    },
  });
};

export const validateUserExists = async (
  setUser: (data: any) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  userId: string,
  useToken: boolean = false,
) => {
  return apiCall({
    method: "get",
    url: `${apiPrefix}/users/${userId}`,
    useToken,
    setLoading,
    idReturn: true,
    onSuccess: (response) => {
      const userData = response.data;
      setUser(MapUser(userData));
      return { user_id: userData.id };
    },
    onError: (errorResponse) => {
      setError(errorResponse);
    },
  });
};

export const getUsersCsv = async (
  setLoading: () => void,
  setError: (error: any) => void,
  userIds: number[],
  deselectedUserIds: number[] = [],
  userType = 1,
) => {
  const url = `${apiPrefix}/users/export_csv`;

  const body = {
    user_ids: userIds,
    deselected_user_ids: deselectedUserIds,
    user_type: userType,
  };

  return apiCall({
    method: "post",
    url: url,
    data: body,
    setLoading,
    onSuccess: (response: any) => {
      const text = response.data;
      const rowCount = text.split("\n").length - 2;
      const csvData = response.data;
      return { csvData, rowCount };
    },
    onError: (errorResponse: any) => {
      setError(errorResponse);
    },
  });
};

export const getUsersData = async (
  setUserData: (data: any) => void,
  setTotalUsers: (count: number) => void,
  setError: (error: any) => void,
  setLoading: (loading: boolean) => void,
  currentPage: number,
  itemsPerPage: number,
  query: string,
  type: USER_TYPE[] = [USER_TYPE.INTERNAL, USER_TYPE.KEYCLOAK_USER],
) => {
  const queryParam = query || null;

  const url = createQuery(`${apiPrefix}/users/`, {
    user_type: type !== 0 ? type : undefined,
    next_page: currentPage,
    per_page: itemsPerPage,
    keyword: queryParam,
  });

  if (currentPage <= 0) {
    currentPage = 1;
  }

  return apiCall({
    method: "get",
    url: url,
    setLoading,
    onSuccess: (response) => {
      const users = response.data.users.map(MapUser);
      setUserData(users);
      setTotalUsers(response.data.total_count);
      setIsSynching(response.data.batch_processing);
    },
    onError: (errorResponse) => {
      setError(errorResponse);
      setIsSynching(false);
    },
  });
};

export const getAdminUserData = async (
  setAdminUser: (data: any) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  tenantId: string,
) => {
  return apiCall({
    method: "get",
    url: `${apiPrefix}/admin_users/${tenantId}`,
    setLoading,
    onSuccess: (response) => {
      setAdminUser(MapAdminUser(response));
    },
    onError: (errorResponse) => {
      setError(errorResponse);
    },
  });
};

export const postUserData = async (
  userData: any,
  userTenantData: any = {},
  userBroadcastGroupData: any = {},
  keycloak_groups = {},
  roleData: any = {},
  appData: any[] = [],
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  forceCreate = false,
) => {
  if (!userData) return;

  const {
    id,
    uid,
    name,
    furigana_name,
    tenant_keycloak_groups,
    ...filteredUserData
  } = userData;
  filteredUserData.user_type = USER_TYPE.KEYCLOAK_USER;

  const apps = appData.map((app) => ({
    id: app.id,
    name: app.name,
  }));

  const requestData = {
    user: {
      ...filteredUserData,
      user_tenant_id: userTenantData?.tenant_department_id || 0,
      role_id: roleData?.id || null,
      position_name: userTenantData?.position_name || "",
      broadcast_group_id: userBroadcastGroupData?.id || null,
      user_keycloak_groups: keycloak_groups,
      apps,
      force_create: forceCreate,
    },
  };

  return apiCall({
    method: "post",
    url: `${apiPrefix}/users`,
    data: requestData,
    idReturn: true,
    setLoading,
    onSuccess: (response) => {
      const responseMessage = handleResponse(response, "電話帳");

      return {
        ...responseMessage,
        user_id: response.data?.created_users?.id,
      };
    },
    onError: (errorResponse) => {
      // console.log("Error response from API:", errorResponse);
      const error =
        errorResponse?.failed_users?.[0]?.error ||
        errorResponse.errors ||
        errorResponse.error ||
        errorResponse;

      setError(error);
    },
  });
};

export const postUserDataArray = async (
  userData: any[],
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (userData) {
    let response;
    let responseMessage;
    let failedUsers;
    let createdUsers;
    let request_id;

    const userDataArray = userData.map((user) => {
      const { id, uid, name, furigana_name, ...filteredUserData } =
        user.userInfo;

      filteredUserData.user_type = USER_TYPE.KEYCLOAK_USER;

      const apps = user.userApps.map((app) => ({
        id: app.id,
        name: app.name,
      }));

      return {
        ...filteredUserData,
        user_tenant_id: user.userTenantData?.tenant_department_id || 0,
        role_id: user.roleData?.id || null,
        position_name: user?.userInfo?.position_name || "",
        apps,
      };
    });

    try {
      const header = store.getState().api.headers;
      api.defaults.headers = header;

      setLoading(true);

      response = await api.post(`${apiPrefix}/users`, {
        user: userDataArray,
      });
      responseMessage = handleResponse(response, "電話帳");
      request_id = response.data.request_id;
    } catch (err) {
      failedUsers = err.response.data.failed_users;

      console.error(err.message);
      createdUsers =
        err.response?.data?.created_users?.filter(
          (user) => user?.login_id !== null,
        ) || [];
      failedUsers =
        err.response?.data?.failed_users?.filter(
          (user) => user?.login_id !== null,
        ) || [];
      const errorResponse =
        err?.response?.data?.errors ||
        err?.response?.data?.error ||
        err?.message;

      responseMessage = { status: err.status || 422, message: errorResponse };
      setError(errorResponse);
    } finally {
      setLoading(false);
      if (response) {
        failedUsers =
          response?.data?.failed_users?.filter(
            (user) => user?.login_id !== null,
          ) || [];
        createdUsers = response.data?.created_users;
        responseMessage = {
          ...responseMessage,
        };
      }

      if (failedUsers.length) {
        failedUsers?.forEach((user) => {
          const translatedMessage = TranslateErrorMessage(user.error);
          user.error = translatedMessage;
        });
      } else {
        const translatedMessage = TranslateErrorMessage(failedUsers.error);
        failedUsers.error = translatedMessage;
      }

      return {
        responseMessage,
        request_id: request_id,
        failed_users: failedUsers,
        created_users: createdUsers,
      };
    }
  }
};

export const postUserDataArrayCSV = async (
  userData: any[],
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (userData) {
    let response;
    let responseMessage;
    let failedUsers;
    let createdUsers;
    let request_id;

    const userDataArray = userData.map((user) => {
      const { uid, name, furigana_name, ...filteredUserData } = user.userInfo;

      const apps = user.userApps.map((app) => ({
        id: app.id,
        name: app.name,
      }));

      return {
        ...filteredUserData,
        user_tenant_id: user.userTenantData?.tenant_department_id || 0,
        role_id: user.roleData?.id || null,
        position_name: user?.userInfo?.position_name || "",
        apps,
      };
    });

    try {
      const header = store.getState().api.headers;
      api.defaults.headers = header;

      setLoading(true);

      response = await api.post(`${apiPrefix}/users/create_with_csv`, {
        user: userDataArray,
      });
      responseMessage = handleResponse(response, "電話帳");
      request_id = response.data.request_id;
    } catch (err) {
      failedUsers = err.response.data.failed_users;

      console.error(err.message);
      createdUsers =
        err.response?.data?.created_users?.filter(
          (user) => user?.login_id !== null,
        ) || [];
      failedUsers =
        err.response?.data?.failed_users?.filter(
          (user) => user?.login_id !== null,
        ) || [];
      const errorResponse =
        err?.response?.data?.errors ||
        err?.response?.data?.error ||
        err?.message;

      responseMessage = { status: err.status || 422, message: errorResponse };
      setError(errorResponse);
    } finally {
      setLoading(false);
      if (response) {
        failedUsers =
          response?.data?.failed_users?.filter(
            (user) => user?.login_id !== null,
          ) || [];
        createdUsers = response.data?.created_users;
        responseMessage = {
          ...responseMessage,
        };
      }

      if (failedUsers.length) {
        failedUsers?.forEach((user) => {
          const translatedMessage = TranslateErrorMessage(user.error);
          user.error = translatedMessage;
        });
      } else {
        const translatedMessage = TranslateErrorMessage(failedUsers.error);
        failedUsers.error = translatedMessage;
      }

      return {
        responseMessage,
        request_id: request_id,
        failed_users: failedUsers,
        created_users: createdUsers,
      };
    }
  }
};

export const putUserData = async (
  userData: any,
  tenantDepartmentData: any,
  userBroadcastGroupData: any,
  roleData: any,
  appData: any[],
  keycloakGroups,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  updateUserId: string = "",
) => {
  if (!userData) return;

  const userId = updateUserId || userData.id;

  const apps = appData.map((app) => ({
    id: app.id,
    name: app.name,
  }));

  const {
    id,
    uid,
    name,
    furigana_name,
    internal_departments,
    tenant_keycloak_groups,
    user_keycloak_groups,
    ...filteredUserData
  } = userData;

  const requestData = {
    user: {
      ...filteredUserData,
      ...(tenantDepartmentData && {
        user_tenant_id: tenantDepartmentData.tenant_department_id,
        position_name: tenantDepartmentData.position_name,
      }),
      role_id: roleData.id,
      broadcast_group_id: userBroadcastGroupData?.id || null,
      keycloak_groups: keycloakGroups,
      apps,
    },
  };

  return apiCall({
    method: "put",
    url: `${apiPrefix}/users/${userId}`,
    data: requestData,
    setLoading,
    onSuccess: (response) => {
      const responseMessage = handleResponse(response, "電話帳");
      return responseMessage;
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const resetUserPassword = async (
  userPassword: string,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
  id = 0,
) => {
  if (!userPassword) return;

  const body = {
    password: userPassword,
  };

  const url =
    id === 0 ? `${apiPrefix}/password/` : `${apiPrefix}/password/${id}`;

  return apiCall({
    method: "put",
    url,
    data: body,
    setLoading,
    onSuccess: (response) => {
      return handleResponse(response, "パスワード変更");
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};

export const uploadUserImage = async (id: string, userImageData: File[]) => {
  const header = store.getState().api.headers;
  api.defaults.headers = header;
  let response;
  let translatedMessage;

  const formData = new FormData();
  formData.append("image", userImageData[0]);

  try {
    response = await api.put(
      `${apiPrefix}/users/${id}/upload_image`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    translatedMessage = handleResponse(response);
  } catch (err) {
    translatedMessage = TranslateErrorMessage(err.data);
  } finally {
    return translatedMessage;
  }
};

export const deleteUserImage = async (
  id: string,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  if (!id) return;

  return apiCall({
    method: "delete",
    url: `${apiPrefix}/users/${id}/delete_image`,
    setLoading,
    onSuccess: (response) => {
      return handleResponse(response, "画像削除");
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(TranslateErrorMessage(error));
    },
  });
};

export const uploadCardImage = async (
  id: string,
  images: File[],
  initialize = false,
) => {
  const header = store.getState().api.headers;
  api.defaults.headers = header;

  const formData = new FormData();

  images.forEach((file, index, local) => {
    if (local) {
      formData.append(`images[${index}]`, file);
    }
  });

  try {
    const { status, data } = await api.put(
      `${apiPrefix}/users/${id}/upload_card_image?initialize=${initialize}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    return {
      status,
      translatedMessage: TranslateErrorMessage(data),
    };
  } catch (err: any) {
    return {
      status: err?.response?.status || 500,
      translatedMessage: TranslateErrorMessage(
        err?.response?.data?.errors ||
          err?.response?.data?.error ||
          err.message,
      ),
    };
  }
};

export const deleteCardImage = async (
  id: string,
  index: number,
  setLoading: (loading: boolean) => void,
  setError: (error: any) => void,
) => {
  const deleteUrl = `${apiPrefix}/users/${id}/delete_image/${index}`;

  return apiCall({
    method: "delete",
    url: deleteUrl,
    setLoading,
    onSuccess: (response) => {
      return handleResponse(response, "カード画像削除");
    },
    onError: (errorResponse) => {
      const error =
        errorResponse.errors || errorResponse.error || errorResponse;
      setError(error);
    },
  });
};
